import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { companyApi } from '@/api';
import {
  Badge,
  BoxAvatar,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Icon,
  LinkButton,
  LoadingState,
  useToast,
} from '@/components/ui';
import { VerifyToContinueButton } from '@/features/verification/CompanyVerificationGate';
import { useCompanyVerification } from '@/features/verification/useCompanyVerification';
import { errorMessage } from '@/hooks/useApiError';
import { usePermissions } from '@/hooks/usePermissions';
import { paths } from '@/routes/paths';
import { PERMISSIONS } from '@/types/roles';
import { formatDate } from '@/utils/format';

// import { NewApplicationsButton } from './NewApplicationsButton';
import { ViewApplicationsButton } from './ViewApplicationsButton';
import { INTERNSHIP_STATUS_LABELS, INTERNSHIP_STATUS_TONES } from './status';

const SHOWN = 6;

/** The box's coloured edge, in the same meaning as its status badge. */
const EDGE_TONES = {
  success: 'border-l-success-500',
  warning: 'border-l-warning-500',
  danger: 'border-l-danger-500',
  neutral: 'border-l-line',
};

/**
 * The company's latest internships, as cards. The full list with every action
 * is the Internships page; this one holds the everyday three: delete (close,
 * for a live listing, which the API will not delete), edit and view.
 *
 * The query keys sit under the Internships page's, so a change on either page
 * refreshes both.
 */
export function DashboardInternships({ companyName }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { can } = usePermissions();
  const { needsVerification } = useCompanyVerification();
  const [pendingDeletion, setPendingDeletion] = useState(null);

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ['company-internships', 'dashboard'],
    queryFn: () => companyApi.listInternships({ page: 1, page_size: SHOWN }),
  });

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ['company-internships'] });
    void queryClient.invalidateQueries({ queryKey: ['company-internship-counts'] });
  }

  const close = useMutation({
    mutationFn: (id) => companyApi.closeInternship(id),
    onSuccess: () => {
      toast.success('Internship closed');
      invalidate();
    },
    onError: (closeError) =>
      toast.error(errorMessage(closeError, 'Could not close the internship')),
  });

  const remove = useMutation({
    mutationFn: (id) => companyApi.deleteInternship(id),
    onSuccess: () => {
      toast.success('Internship deleted');
      invalidate();
      setPendingDeletion(null);
    },
    onError: (deleteError) => {
      toast.error(errorMessage(deleteError, 'Could not delete the internship'));
      setPendingDeletion(null);
    },
  });

  const rows = data?.data ?? [];
  const totalItems = data?.meta?.total_items ?? 0;

  // Posting waits for verification, so the way in says that instead.
  const createInternship =
    can(PERMISSIONS.INTERNSHIP_CREATE) &&
    (needsVerification ? (
      <VerifyToContinueButton />
    ) : (
      <LinkButton to={paths.company.internshipNew}>
        <Icon name="plus" size={16} />
        Create Internship
      </LinkButton>
    ));

  return (
    <section aria-labelledby="your-internships" className="mt-8">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 id="your-internships" className="text-ink text-xl font-semibold tracking-tight">
          Your Internships
        </h2>
        {createInternship}
      </div>

      {isError ? (
        <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
      ) : isPending ? (
        <LoadingState label="Loading your internships…" />
      ) : rows.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Icon name="internship" size={22} />}
            title="Nothing here yet"
            description="Create your first internship to start receiving applications."
          />
        </Card>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {rows.map((row) => (
            /* <Card key={row.id} className="flex flex-col">
              <p className="text-ink-muted text-sm">
                {row.applications_count} total{' '}
                {row.applications_count === 1 ? 'application' : 'applications'}
              </p>
              <h3 className="text-ink mt-2 text-base font-semibold">{row.title}</h3>
              <p className="text-ink-muted mt-0.5 text-sm">{companyName}</p>
              <div className="mt-3">
                <Badge dot tone={INTERNSHIP_STATUS_TONES[row.status] ?? 'neutral'}>
                  {INTERNSHIP_STATUS_LABELS[row.status] ?? row.status}
                </Badge>
              </div>
              <p className="text-ink-muted mt-3 flex items-center gap-1.5 text-sm">
                <Icon name="calendar" size={14} />
                {row.application_deadline ? formatDate(row.application_deadline) : 'No deadline'}
              </p>

              <div className="mt-4 flex flex-1 flex-wrap items-end justify-end gap-2"> */
            <Card
              key={row.id}
              className={`flex flex-col border-l-4 transition hover:-translate-y-0.5 hover:shadow-md ${
                EDGE_TONES[INTERNSHIP_STATUS_TONES[row.status]] ?? EDGE_TONES.neutral
              }`}
            >
              <div className="flex items-start gap-3">
                <BoxAvatar name={row.title} />
                <div className="min-w-0 flex-1">
                  <h3 className="text-ink truncate text-base font-semibold">{row.title}</h3>
                  <p className="text-ink-muted mt-0.5 truncate text-sm">{companyName}</p>
                </div>
                <Badge dot tone={INTERNSHIP_STATUS_TONES[row.status] ?? 'neutral'}>
                  {INTERNSHIP_STATUS_LABELS[row.status] ?? row.status}
                </Badge>
              </div>

              <div className="mt-4 flex flex-wrap gap-2 text-sm">
                <span className="bg-surface-muted text-ink-muted inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5">
                  <Icon name="users" size={14} />
                  <span>
                    {row.applications_count} total{' '}
                    {row.applications_count === 1 ? 'application' : 'applications'}
                  </span>
                </span>
                <span className="bg-surface-muted text-ink-muted inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5">
                  <Icon name="calendar" size={14} />
                  {row.application_deadline ? formatDate(row.application_deadline) : 'No deadline'}
                </span>
              </div>

              <div className="border-line mt-4 flex flex-1 flex-wrap items-end justify-end gap-2 border-t pt-4">
                {/* {can(PERMISSIONS.APPLICATION_READ) && <NewApplicationsButton internship={row} />} */}
                {can(PERMISSIONS.APPLICATION_READ) && <ViewApplicationsButton internship={row} />}
                {row.status === 'PUBLISHED'
                  ? can(PERMISSIONS.INTERNSHIP_PUBLISH) && (
                      <Button
                        variant="tintDanger"
                        size="sm"
                        title={`Close ${row.title}`}
                        isLoading={close.isPending && close.variables === row.id}
                        onClick={() => close.mutate(row.id)}
                      >
                        Close listing
                      </Button>
                    )
                  : can(PERMISSIONS.INTERNSHIP_DELETE) && (
                      <Button
                        variant="tintDanger"
                        size="sm"
                        title={`Delete ${row.title}`}
                        onClick={() => setPendingDeletion(row)}
                      >
                        Delete
                      </Button>
                    )}
                {can(PERMISSIONS.INTERNSHIP_UPDATE) && (
                  <LinkButton
                    to={paths.company.internshipEdit(row.id)}
                    size="sm"
                    title={`Edit ${row.title}`}
                  >
                    Edit
                  </LinkButton>
                )}
                <LinkButton
                  to={paths.company.internshipDetail(row.id)}
                  variant="secondary"
                  size="sm"
                  title={`View ${row.title}`}
                >
                  View Details
                </LinkButton>
              </div>
            </Card>
          ))}
        </div>
      )}

      {totalItems > SHOWN && (
        <div className="mt-4 text-center">
          <LinkButton to={paths.company.internships} variant="secondary" size="sm">
            View all {totalItems} internships
          </LinkButton>
        </div>
      )}

      <ConfirmDialog
        open={Boolean(pendingDeletion)}
        title="Delete internship"
        message={`"${pendingDeletion?.title}" will be removed. This cannot be undone.`}
        confirmLabel="Delete"
        isLoading={remove.isPending}
        onConfirm={() => remove.mutate(pendingDeletion.id)}
        onCancel={() => setPendingDeletion(null)}
      />
    </section>
  );
}
